// Creating a job.
//
// Was an inline panel that expanded on the jobs list, which put a form and
// the list of everything it was about to join on the same screen and gave
// the fields nowhere to breathe. This is Zanetto Tools' shape instead: its
// own page, the actions in the header beside the title, and the fields in a
// two-column grid rather than a single tall stack.
//
// ZT's other new-job fields were deliberately not ported (Nathan, 2026-09-07).
// Start and end dates would be a second answer to "when is this job on?" that
// the bookings already answer and could disagree with; job manager and client
// representatives are ZT machinery with no home here yet. The PO number came
// across, because a principal contractor's PO otherwise ends up buried in the
// description. No money field: ZT's new-job form has none either — it posts a
// hardcoded zero — and a Dayboard job carries no money (round 43 put money on
// quotes only).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scheduling, type Client } from '../schedulingApi';
import { ApiError } from '../api';
import { notifications } from '../notificationsApi';
import { Banner, Field } from '../ui';
import { PageHeader } from '../components/ui/Page';
import { NewClientModal } from '../components/clients/NewClientModal';

export function NewJobPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [title, setTitle] = useState('');
  const [clientId, setClientId] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [jobManagerId, setJobManagerId] = useState('');
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);

  useEffect(() => {
    void scheduling
      .listClients()
      .then((r) => setClients(r.clients))
      .catch(() => setClients([]));
    // The manager-callable name list (the notifications audience picker's).
    // Listing users outright is admin-only, and a manager creating a job must
    // not be refused a picker they are entitled to.
    void notifications
      .people()
      .then((r) => setPeople(r.people))
      .catch(() => setPeople([]));
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const { job } = await scheduling.createJob({
        title,
        clientId: clientId === '' ? null : clientId,
        siteAddress: siteAddress === '' ? null : siteAddress,
        purchaseOrderNumber: purchaseOrderNumber === '' ? null : purchaseOrderNumber,
        description: description === '' ? null : description,
        startDate: startDate === '' ? null : startDate,
        endDate: endDate === '' ? null : endDate,
        ...(people.length > 0 ? { jobManagerId: jobManagerId === '' ? null : jobManagerId } : {}),
      });
      // Straight to the job that was just made — the next thing anyone does is
      // attach the drawing or book somebody on it, and both live there.
      navigate(`/app/jobs/${job.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not create that job.');
      }
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="New job"
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => navigate('/app/jobs')}>
              Cancel
            </button>
            {/* Outside the form, tied to it by id — ZT's arrangement, and what
                lets the primary action sit in the header where the eye goes. */}
            <button type="submit" form="job-form" className="btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create job'}
            </button>
          </>
        }
      />

      {error && <Banner kind="error">{error}</Banner>}

      <section className="panel">
        <form id="job-form" onSubmit={submit} noValidate>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              {/* The label row carries the New client button. `sm:min-h-0
                  sm:py-0` is ZT's fix, and it is load-bearing: a 44px touch
                  target in a label row makes that row taller than a plain
                  label's, which drops this field's select half a line below
                  the input sharing its grid row. */}
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="form-label !mb-0" htmlFor="clientId">
                  Client
                </label>
                <button
                  type="button"
                  className="row-action sm:min-h-0 sm:py-0"
                  onClick={() => setNewClientOpen(true)}
                >
                  New client
                </button>
              </div>
              <select
                id="clientId"
                className="form-select"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              >
                {/* Dayboard allows a job with no client, unlike ZT — internal
                    work is real work and still needs scheduling. */}
                <option value="">None (internal work)</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <Field
              label="Title"
              name="title"
              value={title}
              onChange={setTitle}
              error={fields.title}
              hint="Short and recognisable, like “Smith St drainage”."
            />

            <Field
              label="Site address"
              name="siteAddress"
              value={siteAddress}
              onChange={setSiteAddress}
              error={fields.siteAddress}
              required={false}
              hint="Where the crew is actually going."
            />

            <Field
              label="Purchase order number"
              name="purchaseOrderNumber"
              value={purchaseOrderNumber}
              onChange={setPurchaseOrderNumber}
              error={fields.purchaseOrderNumber}
              required={false}
              hint="If the client issued one. It shows on the job."
              autoCapitalize="none"
              spellCheck={false}
            />

            {/* The dates the CLIENT is told, which are not the crew's days —
                those come from booking people onto the job afterwards. Both
                optional, and most jobs never get one. */}
            <Field
              label="Start date"
              name="startDate"
              type="date"
              value={startDate}
              onChange={setStartDate}
              error={fields.startDate}
              required={false}
            />
            <Field
              label="End date"
              name="endDate"
              type="date"
              value={endDate}
              onChange={setEndDate}
              error={fields.endDate}
              required={false}
              hint="What you told the client. Booking the crew comes next."
            />

            {people.length > 0 && (
              <label htmlFor="jobManagerId">
                Job manager
                <select
                  id="jobManagerId"
                  value={jobManagerId}
                  onChange={(event) => setJobManagerId(event.target.value)}
                >
                  <option value="">Nobody</option>
                  {people.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label htmlFor="description" className="sm:col-span-2">
              Description
              <textarea
                id="description"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
          </div>
        </form>
      </section>

      {newClientOpen && (
        <NewClientModal
          onClose={() => setNewClientOpen(false)}
          onCreated={async (created) => {
            // Re-read the list rather than pushing the new row onto it, so the
            // order and anything the server normalised are the server's. If
            // that read fails the client still EXISTS — so fall back to the
            // row we were handed rather than reporting a failure for work
            // that succeeded and losing the selection with it.
            try {
              const r = await scheduling.listClients();
              setClients(r.clients);
            } catch {
              setClients((was) => [...was, created]);
            }
            setClientId(created.id);
            setNewClientOpen(false);
          }}
        />
      )}
    </>
  );
}
